import React from "react"

import {
	Box,
	Button,
	Grid
} from "@mui/material"

import {ErrorDialog, Loading} from "@sinossi/mates-react-library"

import {t, Trans} from "@lingui/macro"

import withUseRouter, { UseRouterParams } from "services/router"
import {
	MatesComponent,
	MatesComponentState,
} from "components/mates-component"
import { DocumentsTable } from "components/documents/documents-table"
import {
	PageHeader,
	PageSectionTitle,
	RoutesData,
} from "components/commons"
import {
	OrderType,
 	SessionResponse,
} from "models"
import {DocumentServices} from "../../services/document.services";
import UploadDocumentModal from "../../components/asset/asset-details/asset-documents-table/upload-document-modal";
import {DeleteDocumentModal} from "../../components/asset/asset-details/asset-documents-table/delete-document-modal";
import {SbSnackbar} from "../../components/commons/sb-snackbar";

interface DocumentsProps extends UseRouterParams
{
	sessionData: SessionResponse
}
interface DocumentsState extends MatesComponentState {
	loading: boolean
	documents: any
	pagination: {
		pageSize: number,
		pageId: number,
		orderType: OrderType|null,
		orderBy: string|null
	}
	documentId: string
	openModal: boolean
	snackbarData: {
		openNotification: boolean,
		severity: 'error' | 'info' | 'success' | 'warning',
		notificationMessage: string
	}
}

class Documents extends React.Component<DocumentsProps, DocumentsState>
{
	private readonly matesComponent: MatesComponent<DocumentsProps, DocumentsState > = new MatesComponent(this)

	constructor(props: any)
	{
		super(props)
		this.state = {
			...this.matesComponent.setupState(),
			loading: true,
			documents: null,
			pagination: {
				pageSize: 10,
				pageId: 0,
				orderType: null,
				orderBy: null
			},
			documentId: "",
			openModal: false,
			snackbarData: {
				openNotification: false,
				severity: "info",
				notificationMessage: ""
			}
		}
	}

	componentDidMount() {
		this.getDocuments()
	}

	private getDocuments = () => {
		DocumentServices.getDocuments(this.state.pagination, this.getDocumentsCallback, this.matesComponent.onException)

	}

	private getDocumentsCallback = (response: any) => {
		if(response.hasError()) {
			this.matesComponent.onError(response)
			return
		}

		this.setState(() => ({
			loading: false,
			documents: response.data,
		}))
	}

	private onPaginationChanged = (pageId: number, pageSize: number): void => {
		this.setState(
			() => ({
				pagination: {
					...this.state.pagination,
					pageSize: pageSize,
					pageId: pageId,
				},
			}), this.getDocuments
		)
	}

	private onSortChanged = (orderBy: string|null, orderType: OrderType|null): void => {
		this.setState(
			() => ({
				pagination: {
					pageSize: this.state.pagination.pageSize,
					pageId: this.state.pagination.pageId,
					orderBy: orderBy,
					orderType: orderType,
				},
			}), this.getDocuments
		)
	}

	private deleteDocument = (documentId: string) => {
		if(this.state.documents) {
			this.setState(() => ({
				loading: true
			}), () => DocumentServices.deleteDocument(documentId, this.deleteDocumentCallback, this.matesComponent.onException))
		}
	}

	private deleteDocumentCallback = (response: any) => {
		if(response.hasError()) {
			this.matesComponent.onError(response)
			return
		}

		this.setState(() => ({
			loading: false
		}))
	}

	private handleOpenNotification = (message: string, severity: 'error' | 'info' | 'success' | 'warning') => {
		this.setState(() => ({
			snackbarData: {
				openNotification: true,
				notificationMessage: message,
				severity: severity
			}
		}))
	};

	private handleCloseNotification = () => {
		this.setState(() => ({
			snackbarData: {
				...this.state.snackbarData,
				openNotification: false
			}
		}))
	};

	private downloadError = (error: any, originalResponse: any): void => {
		this.handleOpenNotification(t({id: "download-error", message: "Si è verificato un errore nel download."}), 'error')
	}

	private copyDocumentLink = (documentId: string): void => {
		const baseUrl = process.env.REACT_APP_BASE_URL
		const documentLink = `${baseUrl}/api/document/${documentId}`
		
		navigator.clipboard.writeText(documentLink).then(() => {
			this.handleOpenNotification(t({id: "copy-link-success", message: "Link copiato negli appunti"}), 'success')
		}).catch(() => {
			this.handleOpenNotification(t({id: "copy-link-error", message: "Errore durante la copia del link"}), 'error')
		})
	}

	private handleOpenModal = (): void => {
		this.setState(() => ({
			openModal: true
		}))
	}

	public handleCloseModal = (data?: FormData) => {
		if(data){
			this.setState(() => ({
				openModal: false
			}), () => (this.uploadDocuments(data)))
		}
		else {
			this.setState(() => ({
				openModal: false
			}))
		}
	}

	private uploadDocuments(data: FormData){
		DocumentServices.uploadDocument(data, this.uploadDocumentsCallback, this.matesComponent.onException)
	}

	private uploadDocumentsCallback = (response: any) => {
		if(response.hasError()) {
			this.matesComponent.onError(response)
			return
		}

		this.getDocuments()
	}

	render()
	{
		return (
			<Box sx={{ pt: 2 }}>
				<Loading show={this.state.loading} />
				<PageHeader
					title={RoutesData.DOCUMENTS.title}
					paths={[ RoutesData.DASHBOARD, RoutesData.DOCUMENTS ]}
				/>

				{!this.state.loading && <>
					<Box sx={{ mt: 3, bgcolor: "background.default" }}>
						<PageSectionTitle title={t({ id: "documents.title", message: "Documentazione Legale e Compliance" })} />
						{
							this.state.documents &&
							<Grid container>
								<Grid item xs={12} lg={12} sx={{p: 2, pr: 1}}>
									<DocumentsTable
										data={this.state.documents}
										pagination={this.state.pagination}
										onSortChanged={this.onSortChanged}
										onPaginationChanged={this.onPaginationChanged}
										deleteDocument={this.deleteDocument}
										copyDocumentLink={this.copyDocumentLink}
										downloadBaseUrl={`/api/document/`}
										onDownloadError={this.downloadError}
										sessionData={this.props.sessionData}
									/>
								</Grid>
								<Grid item xs={12}>
									<Box sx={{marginY: 2, textAlign: "center" }}>
										<Button
											variant="contained"
											color="secondary"
											size="small"
											onClick={this.handleOpenModal}
										>
											<Trans id="documents-table.upload-document">
												Carica documento
											</Trans>
										</Button>
									</Box>
								</Grid>
							</Grid>
						}
						{
							this.state.openModal &&
							<UploadDocumentModal open={this.state.openModal} onClose={this.handleCloseModal} callbackUploadDocument={this.handleCloseModal} isFromDocumentsPage={true}/>
						}
					</Box>
				</>}
				<SbSnackbar snackbarData={this.state.snackbarData} handleCloseNotification={this.handleCloseNotification} />

			</Box >
		)
	}
}

export default withUseRouter(Documents)
